import { create } from "zustand";
import { supabase } from "@/integrations/supabase/client";
import { AdminClassRating } from "@/types/types";
import { getUserGymId } from "@/utils/gymAuth";
import i18n from "@/i18n";

interface AdminRatingsState {
  ratings: AdminClassRating[];
  isLoading: boolean;
  error: string | null;
  fetchRatings: () => Promise<void>;
}

export const useAdminRatingsStore = create<AdminRatingsState>((set) => ({
  ratings: [],
  isLoading: false,
  error: null,

  fetchRatings: async () => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from("admin_class_ratings")
        .select("*")
        .eq('gym_id', getUserGymId())
        .order("created_at", { ascending: false });

      if (error) throw error;

      const ratings = (data || []) as AdminClassRating[];

      // Enrich with assigned coach name from class_booking_coach + user_profiles
      const bookingIds = Array.from(
        new Set(ratings.map(r => (r as any).booking_id).filter(Boolean))
      ) as string[];

      let coachByBooking = new Map<string, string>();

      if (bookingIds.length > 0) {
        const { data: coachData, error: coachErr } = await supabase
          .from("class_booking_coach")
          .select("class_booking_id, coach:user_profiles!class_booking_coach_coach_user_profile_fkey(first_name, last_name)")
          .in("class_booking_id", bookingIds);

        if (!coachErr && coachData) {
          coachData.forEach((row: any) => {
            const fn = row.coach?.first_name?.trim() || "";
            const ln = row.coach?.last_name?.trim() || "";
            const name = `${fn} ${ln}`.trim();
            if (name) coachByBooking.set(row.class_booking_id, name);
          });
        }
      }

      const enriched = ratings.map(r => {
        const assigned = coachByBooking.get((r as any).booking_id);
        const fallback =
          r.instructor && r.instructor !== "Default Instructor"
            ? r.instructor
            : i18n.t("common:notAssigned");
        return { ...r, instructor: assigned ?? fallback };
      });

      set({
        ratings: enriched,
        isLoading: false,
        error: null,
      });
    } catch (err: any) {
      set({
        error: err.message || "Failed to fetch ratings",
        isLoading: false,
      });
    }
  },
}));
